import { useState } from "react";
import axios from "axios";
import { API, USER_ID } from "../App";
import { User, Ruler, Scales, Target, Fire, Lightning, Bread, Drop, Check } from "@phosphor-icons/react";
import { toast } from "sonner";

const Profile = ({ profile, setProfile }) => {
  const [formData, setFormData] = useState({
    name: profile?.name || "",
    email: profile?.email || "",
    weight: profile?.weight || "",
    height: profile?.height || "",
    goal_weight: profile?.goal_weight || "",
    daily_calorie_goal: profile?.daily_calorie_goal || 2000,
    daily_protein_goal: profile?.daily_protein_goal || 150,
    daily_carbs_goal: profile?.daily_carbs_goal || 250,
    daily_fat_goal: profile?.daily_fat_goal || 65,
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/profile/${USER_ID}`, {
        ...profile,
        ...formData,
        weight: formData.weight ? Number(formData.weight) : null,
        height: formData.height ? Number(formData.height) : null,
        goal_weight: formData.goal_weight ? Number(formData.goal_weight) : null,
        daily_calorie_goal: Number(formData.daily_calorie_goal),
        daily_protein_goal: Number(formData.daily_protein_goal),
        daily_carbs_goal: Number(formData.daily_carbs_goal),
        daily_fat_goal: Number(formData.daily_fat_goal),
      });
      setProfile(response.data);
      toast.success("Profil mis à jour !");
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const bmi = formData.weight && formData.height ? (formData.weight / Math.pow(formData.height / 100, 2)).toFixed(1) : null;
  const getBmiCategory = (bmi) => {
    if (!bmi) return null;
    if (bmi < 18.5) return { label: "Insuffisance pondérale", color: "text-blue-500" };
    if (bmi < 25) return { label: "Poids normal", color: "text-green-500" };
    if (bmi < 30) return { label: "Surpoids", color: "text-amber-500" };
    return { label: "Obésité", color: "text-red-500" };
  };
  const bmiCategory = getBmiCategory(bmi);

  return (
    <div className="p-4 md:p-8 animate-fadeIn max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="font-display text-3xl uppercase tracking-tight text-charcoal">Ton profil</h1>
        <p className="text-gray-500">Règle tes informations, tes objectifs et tes repères nutritionnels.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Personal Info */}
          <div className="card p-5" data-testid="personal-info">
            <h2 className="font-semibold text-charcoal mb-4 flex items-center gap-2">
              <User size={20} className="text-gold" /> Informations
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-gray-500 mb-1 block">Nom</label>
                <input type="text" name="name" value={formData.name} onChange={handleChange} className="w-full px-4 py-3 rounded-xl border border-gray-200" data-testid="profile-name" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-gray-500 mb-1 block">Email</label>
                <input type="email" name="email" value={formData.email} onChange={handleChange} className="w-full px-4 py-3 rounded-xl border border-gray-200" data-testid="profile-email" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-gray-500 mb-1 block">Taille (cm)</label>
                <input type="number" name="height" value={formData.height} onChange={handleChange} className="w-full px-4 py-3 rounded-xl border border-gray-200" data-testid="profile-height" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-gray-500 mb-1 block">Poids (kg)</label>
                <input type="number" name="weight" step="0.1" value={formData.weight} onChange={handleChange} className="w-full px-4 py-3 rounded-xl border border-gray-200" data-testid="profile-weight" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-gray-500 mb-1 block">Poids objectif (kg)</label>
                <input type="number" name="goal_weight" step="0.1" value={formData.goal_weight} onChange={handleChange} className="w-full px-4 py-3 rounded-xl border border-gray-200" data-testid="profile-goal-weight" />
              </div>
            </div>
          </div>

          {/* Nutrition Goals */}
          <div className="card p-5" data-testid="nutrition-goals">
            <h2 className="font-semibold text-charcoal mb-4 flex items-center gap-2">
              <Fire size={20} className="text-gold" /> Objectifs journaliers
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-gray-500 mb-1 flex items-center gap-1"><Fire size={12} className="text-gold" /> Calories</label>
                <input type="number" name="daily_calorie_goal" value={formData.daily_calorie_goal} onChange={handleChange} className="w-full px-4 py-3 rounded-xl border border-gray-200" data-testid="goal-calories" />
                <span className="text-xs text-gray-500">kcal</span>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-gray-500 mb-1 flex items-center gap-1"><Lightning size={12} className="text-red-500" /> Protéines</label>
                <input type="number" name="daily_protein_goal" value={formData.daily_protein_goal} onChange={handleChange} className="w-full px-4 py-3 rounded-xl border border-gray-200" data-testid="goal-protein" />
                <span className="text-xs text-gray-500">grammes</span>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-gray-500 mb-1 flex items-center gap-1"><Bread size={12} className="text-teal-500" /> Glucides</label>
                <input type="number" name="daily_carbs_goal" value={formData.daily_carbs_goal} onChange={handleChange} className="w-full px-4 py-3 rounded-xl border border-gray-200" data-testid="goal-carbs" />
                <span className="text-xs text-gray-500">grammes</span>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-gray-500 mb-1 flex items-center gap-1"><Drop size={12} className="text-amber-500" /> Lipides</label>
                <input type="number" name="daily_fat_goal" value={formData.daily_fat_goal} onChange={handleChange} className="w-full px-4 py-3 rounded-xl border border-gray-200" data-testid="goal-fat" />
                <span className="text-xs text-gray-500">grammes</span>
              </div>
            </div>
          </div>

          <button onClick={handleSave} disabled={saving} className="w-full btn-primary flex items-center justify-center gap-2" data-testid="save-profile">
            {saving ? "Sauvegarde..." : <><Check size={20} /> Sauvegarder</>}
          </button>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {bmi && (
            <div className="card p-5" data-testid="bmi-card">
              <h3 className="text-xs uppercase tracking-wider text-gray-500 mb-2">IMC</h3>
              <p className="text-4xl font-bold text-charcoal">{bmi}</p>
              {bmiCategory && <p className={`text-sm ${bmiCategory.color}`}>{bmiCategory.label}</p>}
            </div>
          )}

          <div className="card p-5" data-testid="macro-distribution">
            <h3 className="text-xs uppercase tracking-wider text-gray-500 mb-4">Répartition macros</h3>
            {(() => {
              const totalCals = (formData.daily_protein_goal * 4) + (formData.daily_carbs_goal * 4) + (formData.daily_fat_goal * 9);
              const proteinPct = Math.round((formData.daily_protein_goal * 4) / totalCals * 100);
              const carbsPct = Math.round((formData.daily_carbs_goal * 4) / totalCals * 100);
              const fatPct = Math.round((formData.daily_fat_goal * 9) / totalCals * 100);
              return (
                <>
                  <div className="flex h-4 rounded-full overflow-hidden mb-4">
                    <div className="bg-red-400" style={{ width: `${proteinPct}%` }} />
                    <div className="bg-teal-400" style={{ width: `${carbsPct}%` }} />
                    <div className="bg-amber-400" style={{ width: `${fatPct}%` }} />
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center text-xs">
                    <div><div className="w-3 h-3 rounded-full bg-red-400 mx-auto mb-1" /><span className="text-gray-500">Prot. {proteinPct}%</span></div>
                    <div><div className="w-3 h-3 rounded-full bg-teal-400 mx-auto mb-1" /><span className="text-gray-500">Gluc. {carbsPct}%</span></div>
                    <div><div className="w-3 h-3 rounded-full bg-amber-400 mx-auto mb-1" /><span className="text-gray-500">Lip. {fatPct}%</span></div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
